import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn } from '../common/utils';
import { createSha256Transformer, createTimestampTransformer } from '../common/sql';
import {
  DBDocumentPageSchema,
  DBDocumentIndexEntrySchema,
  DBDocumentDigitisationRunSchema,
  DBSourceDocumentSchema,
  DocumentPageSchema,
  DocumentIndexEntrySchema,
  DocumentDigitisationRunSchema,
  SourceDocumentSchema,
} from './schemas';

export const SourceDocumentCodecConfig: TransformerConfig<
  typeof SourceDocumentSchema,
  typeof DBSourceDocumentSchema
> = {
  name: 'SourceDocument',
  clientSchema: SourceDocumentSchema,
  dbSchema: DBSourceDocumentSchema,
  transformers: [
    createSha256Transformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>('sha256'),
    createSha256Transformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>(
      'contentFingerprint',
      { nullable: true }
    ),
    createTimestampTransformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>(
      'updatedAt',
      { nullable: true }
    ),
    createTimestampTransformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>(
      'handwritingDecidedAt',
      { nullable: true }
    ),
  ],
};

export const DocumentPageCodecConfig: TransformerConfig<
  typeof DocumentPageSchema,
  typeof DBDocumentPageSchema
> = {
  name: 'DocumentPage',
  clientSchema: DocumentPageSchema,
  dbSchema: DBDocumentPageSchema,
  transformers: [
    createTimestampTransformer<typeof DocumentPageSchema, typeof DBDocumentPageSchema>('createdAt'),
  ],
};

export const DocumentIndexEntryCodecConfig: TransformerConfig<
  typeof DocumentIndexEntrySchema,
  typeof DBDocumentIndexEntrySchema
> = {
  name: 'DocumentIndexEntry',
  clientSchema: DocumentIndexEntrySchema,
  dbSchema: DBDocumentIndexEntrySchema,
  transformers: [
    createTimestampTransformer<typeof DocumentIndexEntrySchema, typeof DBDocumentIndexEntrySchema>(
      'createdAt'
    ),
  ],
};

export const DocumentDigitisationRunCodecConfig: TransformerConfig<
  typeof DocumentDigitisationRunSchema,
  typeof DBDocumentDigitisationRunSchema
> = {
  name: 'DocumentDigitisationRun',
  clientSchema: DocumentDigitisationRunSchema,
  dbSchema: DBDocumentDigitisationRunSchema,
  transformers: [
    createSha256Transformer<
      typeof DocumentDigitisationRunSchema,
      typeof DBDocumentDigitisationRunSchema
    >('configHash'),
    createTimestampTransformer<
      typeof DocumentDigitisationRunSchema,
      typeof DBDocumentDigitisationRunSchema
    >('startedAt', { nullable: true }),
    createTimestampTransformer<
      typeof DocumentDigitisationRunSchema,
      typeof DBDocumentDigitisationRunSchema
    >('finishedAt', { nullable: true }),
    createTimestampTransformer<
      typeof DocumentDigitisationRunSchema,
      typeof DBDocumentDigitisationRunSchema
    >('createdAt'),
    createTimestampTransformer<
      typeof DocumentDigitisationRunSchema,
      typeof DBDocumentDigitisationRunSchema
    >('updatedAt', { nullable: true }),
  ],
};

export const encodeSourceDocumentToDb = createEncodeToDbFn(SourceDocumentCodecConfig);
export const decodeSourceDocumentFromDb = createDecodeFromDbFn(SourceDocumentCodecConfig);
export const encodeDocumentPageToDb = createEncodeToDbFn(DocumentPageCodecConfig);
export const decodeDocumentPageFromDb = createDecodeFromDbFn(DocumentPageCodecConfig);
export const encodeDocumentIndexEntryToDb = createEncodeToDbFn(DocumentIndexEntryCodecConfig);
export const decodeDocumentIndexEntryFromDb = createDecodeFromDbFn(DocumentIndexEntryCodecConfig);
export const encodeDocumentDigitisationRunToDb = createEncodeToDbFn(
  DocumentDigitisationRunCodecConfig
);
export const decodeDocumentDigitisationRunFromDb = createDecodeFromDbFn(
  DocumentDigitisationRunCodecConfig
);
